import { useSearchParams } from 'react-router';
import { useAIInteractions } from '@/hooks/use-seeds';
import { AIInteractionsTable } from '@/components/analytics/ai-interactions-table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { ArrowUpDown } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';

export default function AIInteractionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page') ?? '1');
  const perPage = Number(searchParams.get('per_page') ?? '20');
  const interactionType = searchParams.get('interaction_type') ?? 'all';
  const success = searchParams.get('success') ?? 'all';
  const sortOrder = searchParams.get('sort_order') ?? 'desc';

  const { data, isLoading } = useAIInteractions({
    page,
    per_page: perPage,
    interaction_type: interactionType === 'all' ? undefined : interactionType,
    success: success === 'all' ? undefined : success === 'true',
    sort_order: sortOrder,
  });

  const updateParam = (key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(key, value);
      if (key !== 'page') next.set('page', '1');
      return next;
    });
  };

  const totalPages = data ? Math.ceil(data.total / perPage) : 0;

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">AI Interaction Logs</h2>

      <div className="flex flex-wrap items-center gap-3">
        <Select value={interactionType} onValueChange={(v) => v && updateParam('interaction_type', v)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="analyze_problem">Analyze Problem</SelectItem>
            <SelectItem value="generate_practices">Generate Practices</SelectItem>
            <SelectItem value="motivation">Motivation</SelectItem>
            <SelectItem value="progress_analysis">Progress Analysis</SelectItem>
            <SelectItem value="feedback_analysis">Feedback Analysis</SelectItem>
          </SelectContent>
        </Select>

        <Select value={success} onValueChange={(v) => v && updateParam('success', v)}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="true">Success</SelectItem>
            <SelectItem value="false">Failed</SelectItem>
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          size="icon"
          onClick={() => updateParam('sort_order', sortOrder === 'asc' ? 'desc' : 'asc')}
        >
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <>
          <AIInteractionsTable interactions={data?.interactions ?? []} />
          {totalPages > 1 && (
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => updateParam('page', String(Math.max(1, page - 1)))}
                    aria-disabled={page <= 1}
                    className={page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const p = i + 1;
                  return (
                    <PaginationItem key={p}>
                      <PaginationLink
                        isActive={p === page}
                        onClick={() => updateParam('page', String(p))}
                        className="cursor-pointer"
                      >
                        {p}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => updateParam('page', String(Math.min(totalPages, page + 1)))}
                    aria-disabled={page >= totalPages}
                    className={page >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </>
      )}
    </div>
  );
}
